import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchApplications } from '../../api/applications';
import { useEmailTemplates } from '../../api/email-templates';
import { type MailingChannel, type MailingListCreateInput, type MailingListDto, type MailingListPatchInput } from '../../api/mailing-lists';
import { Button, Field, Input, Select, Spinner, Textarea } from '../../components/ui';
import {
  DEFAULT_MAILING_TYPE,
  LIST_CHANNEL_OPTIONS,
  SMS_PLACEHOLDER_SENDER,
  KNOWN_SENDERS,
  SELECTION_STATUSES,
  SELECTION_UI_MODES,
  STATUS_LABEL,
  channelLabel,
  channelOf,
  formatWhoMatch,
  parseIdsTextarea,
  pickWhoMatch,
  buildSelection,
  type SelectionUiMode,
} from './lists-model';

/**
 * The create/edit fold of a mailing list (#34.2/#34.4, T.Z132).
 *
 * ONE form for both verbs on purpose — the fields are the same five (name,
 * type label, From, template pin, selection); edit only differs in sending the
 * CHANGED fields (the api re-resolves on any change, so a no-op patch would
 * waste a full resolve). The template picker reads #33.1 and offers LIVE
 * templates only — the api refuses an archived pin with 409 TEMPLATE_ARCHIVED —
 * except for a list already pinned to one, whose pin stays visible (a patch
 * that does not touch it never re-pins).
 */

type UniqueFields = { name: string; mailingType: string; fromEmail: string };

const FIELDS: UniqueFields = { name: '', mailingType: DEFAULT_MAILING_TYPE, fromEmail: KNOWN_SENDERS[0] ?? '' };

/** The template's own channel — an api older than v1.9.72 means EMAIL. */
function templateChannel(template: { channel?: MailingChannel }): MailingChannel {
  return template.channel ?? 'EMAIL';
}

export interface MailingListFormProps<T> {
  mode: 'create' | 'edit';
  initial?: MailingListDto;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  /** Create answers the created list (the page opens it); edit answers nothing. */
  onSubmit: (input: T) => void;
  onCancel?: () => void;
}

export function MailingListForm<T extends MailingListCreateInput | MailingListPatchInput>({
  mode,
  initial,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: MailingListFormProps<T>) {
  const [fields, setFields] = useState<UniqueFields>(
    initial
      ? { name: initial.name, mailingType: initial.mailingType, fromEmail: initial.fromEmail }
      : FIELDS,
  );
  // T.Z210 — the CHANNEL: the owner's «При создании списка выбрать тип sms или
  // email». Edit shows the stored channel read-only (a change would re-pin the
  // template and re-resolve the whole campaign — the create fold decides).
  const [channel, setChannel] = useState<MailingChannel>(initial ? channelOf(initial) : 'EMAIL');
  const [templateId, setTemplateId] = useState<number | null>(initial?.template.templateId ?? null);
  const [templateVersion, setTemplateVersion] = useState<number | null>(initial?.template.templateVersion ?? null);
  const [uiMode, setUiMode] = useState<SelectionUiMode>(initial ? uiModeOf(initial) : 'STATUSES');
  const [oneId, setOneId] = useState<string>(initial?.selectionMode === 'IDS' ? firstStoredId(initial) : '');
  const [idsText, setIdsText] = useState<string>(initial?.selectionMode === 'IDS' ? storedIds(initial).join(', ') : '');
  const [statusOne, setStatusOne] = useState<string>(
    initial?.selectionMode === 'STATUS' && typeof initial.selectionPayload['status'] === 'string'
      ? (initial.selectionPayload['status'] as string)
      : 'EVI_PASSED',
  );
  const [statuses, setStatuses] = useState<string[]>(
    initial?.selectionMode === 'STATUSES' ? storedStatuses(initial) : [],
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const templates = useEmailTemplates();
  const pickable = useMemo(
    () =>
      (templates.data?.data.templates ?? []).filter(
        (template) => template.archivedAt === null && template.latestVersion !== null && templateChannel(template) === channel,
      ),
    [templates.data, channel],
  );

  // A fresh picker answer fills the pin once: the first live template of THIS
  // channel, its latest version.
  useEffect(() => {
    const first = pickable[0];
    if (templateId !== null || !first) return;
    setTemplateId(first.id);
    setTemplateVersion(first.latestVersion);
  }, [pickable, templateId]);

  const selectedTemplate = pickable.find((template) => template.id === templateId) ?? null;
  const versions = selectedTemplate?.versions ?? [];

  const whoEnabled = mode === 'create' && uiMode === 'ONE_ID' && oneId.trim().length > 0;
  const who = useQuery({
    queryKey: ['mailing-lists', 'who', oneId.trim()],
    enabled: whoEnabled,
    retry: false,
    queryFn: ({ signal }) => fetchApplications({ q: oneId.trim(), pageSize: 5 }, signal),
  });
  const whoMatch = who.data ? pickWhoMatch(who.data.data.applications, oneId) : null;

  const submit = () => {
    const built = buildSelection({
      mode: uiMode,
      ids: uiMode === 'ONE_ID' ? [oneId] : parseIdsTextarea(idsText),
      statuses: uiMode === 'STATUS' ? [statusOne] : statuses,
    });
    if (!built.ok) {
      // The RU refusal rides the same line the api's errors do — no extra state.
      setLocalError(built.error);
      return;
    }
    setLocalError(null);
    if (mode === 'create') {
      if (templateId === null || templateVersion === null) {
        setLocalError('Выберите шаблон и версию письма.');
        return;
      }
      const input: MailingListCreateInput = {
        name: fields.name.trim(),
        channel,
        mailingType: fields.mailingType.trim(),
        fromEmail: fields.fromEmail.trim(),
        templateId,
        templateVersion,
        selectionMode: built.selectionMode,
        selectionPayload: built.selectionPayload,
      };
      if (input.name.length === 0 || input.mailingType.length === 0 || input.fromEmail.length === 0) {
        setLocalError('Заполните название, тип и адрес отправителя.');
        return;
      }
      if (input.channel === 'SMS' && templateChannel(selectedTemplate ?? { channel: undefined }) !== 'SMS') {
        // The api refuses the pin with a 400 — the form refuses earlier, in RU.
        setLocalError('Для SMS-списка выберите SMS-шаблон (создайте его на вкладке «Шаблоны»).');
        return;
      }
      // The generic only shapes the CALLER's side; the form itself knows which
      // verb it is running, so the assertion is the mode split above.
      onSubmit(input as T);
      return;
    }
    // Edit: only the CHANGED fields travel — any field re-resolves the list.
    const patch: MailingListPatchInput = {};
    if (initial) {
      if (fields.name.trim() !== initial.name) patch.name = fields.name.trim();
      if (fields.mailingType.trim() !== initial.mailingType) patch.mailingType = fields.mailingType.trim();
      if (fields.fromEmail.trim() !== initial.fromEmail) patch.fromEmail = fields.fromEmail.trim();
      if (templateId !== initial.template.templateId) patch.templateId = templateId ?? undefined;
      if (templateVersion !== initial.template.templateVersion) patch.templateVersion = templateVersion ?? undefined;
    }
    if (initial && channel !== channelOf(initial)) patch.channel = channel;
    if (built.selectionMode !== initial?.selectionMode || JSON.stringify(built.selectionPayload) !== JSON.stringify(initial?.selectionPayload)) {
      patch.selectionMode = built.selectionMode;
      patch.selectionPayload = built.selectionPayload;
    }
    if (Object.keys(patch).length === 0) {
      setLocalError('Нечего менять — измените хотя бы одно поле (любое изменение пересчитает состав).');
      return;
    }
    onSubmit(patch as T);
  };

  const shownError = localError ?? error;

  return (
    <form
      className="mailings-list-form"
      data-testid="mailing-list-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className="mailings-list-form-selection" data-testid="mailing-list-channel">
        <legend>Канал рассылки</legend>
        <div className="mailings-list-form-modes" role="radiogroup" aria-label="Канал рассылки">
          {LIST_CHANNEL_OPTIONS.map((option) => (
            <label key={option.value} className="mailings-list-form-mode" title={option.hint}>
              <input
                type="radio"
                name="mailing-list-channel"
                value={option.value}
                checked={channel === option.value}
                disabled={mode === 'edit'}
                onChange={() => {
                  setChannel(option.value);
                  // The pin is channel-bound: switching the channel drops it so
                  // the picker refills with THIS channel's templates.
                  setTemplateId(null);
                  setTemplateVersion(null);
                }}
                data-testid={`mailing-list-channel-${option.value}`}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {mode === 'edit' ? (
          <p className="mailings-list-form-who">
            Канал созданного списка не меняется — {channelLabel(channel)}. Новый список того же состава создаётся заново.
          </p>
        ) : null}
      </fieldset>

      <div className="mailings-list-form-grid">
        <Field label="Название списка" htmlFor="mailing-list-name" required>
          <Input
            id="mailing-list-name"
            value={fields.name}
            placeholder="Не подписали договор — поток 1"
            onChange={(event) => setFields((prev) => ({ ...prev, name: event.target.value }))}
            data-testid="mailing-list-name"
          />
        </Field>
        <Field label="Тип рассылки (подпись)" htmlFor="mailing-list-type" required>
          <Input
            id="mailing-list-type"
            value={fields.mailingType}
            onChange={(event) => setFields((prev) => ({ ...prev, mailingType: event.target.value }))}
            data-testid="mailing-list-type"
          />
        </Field>
        <Field
          label={channel === 'SMS' ? 'От кого (не используется для SMS)' : 'От кого'}
          htmlFor="mailing-list-from"
          required
          hint={channel === 'SMS' ? 'Отправитель SMS — подпись SMS Aero, её задаёт окружение. Адрес в поле хранится, но в сообщении не участвует.' : undefined}
        >
          <Select
            id="mailing-list-from"
            value={fields.fromEmail}
            disabled={channel === 'SMS'}
            onChange={(event) => setFields((prev) => ({ ...prev, fromEmail: event.target.value }))}
            data-testid="mailing-list-from"
          >
            {(channel === 'SMS' && !KNOWN_SENDERS.includes(SMS_PLACEHOLDER_SENDER)
              ? [SMS_PLACEHOLDER_SENDER, ...KNOWN_SENDERS]
              : KNOWN_SENDERS
            ).map((sender) => (
              <option key={sender} value={sender}>
                {sender}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mailings-list-form-grid mailings-list-form-grid--pin">
        <Field
          label={channel === 'SMS' ? 'Шаблон SMS (только живые SMS-шаблоны)' : 'Шаблон письма'}
          htmlFor="mailing-list-template"
          required
        >
          <Select
            id="mailing-list-template"
            value={templateId === null ? '' : String(templateId)}
            onChange={(event) => {
              const id = Number(event.target.value);
              setTemplateId(id);
              const next = pickable.find((template) => template.id === id);
              setTemplateVersion(next?.latestVersion ?? null);
            }}
            data-testid="mailing-list-template"
          >
            {selectedTemplate === null && templateId !== null ? (
              <option value={templateId}>{initial?.template.title ?? 'текущий шаблон'}</option>
            ) : null}
            {pickable.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Версия письма" htmlFor="mailing-list-version" required>
          {templates.isPending ? (
            <Spinner label="Читаем шаблоны" />
          ) : (
            <Select
              id="mailing-list-version"
              value={templateVersion === null ? '' : String(templateVersion)}
              onChange={(event) => setTemplateVersion(Number(event.target.value))}
              data-testid="mailing-list-version"
            >
              {versions.map((version) => (
                <option key={version.version} value={version.version}>
                  версия {version.version}
                  {version.changelogNote ? ` — ${version.changelogNote}` : ''}
                </option>
              ))}
              {selectedTemplate === null && templateVersion !== null ? <option value={templateVersion}>версия {templateVersion}</option> : null}
            </Select>
          )}
        </Field>
      </div>

      <fieldset className="mailings-list-form-selection">
        <legend>Кого включить</legend>
        <div className="mailings-list-form-modes" role="radiogroup" aria-label="Способ отбора">
          {SELECTION_UI_MODES.map((option) => (
            <label key={option.value} className="mailings-list-form-mode" title={option.hint}>
              <input
                type="radio"
                name="mailing-list-mode"
                value={option.value}
                checked={uiMode === option.value}
                onChange={() => setUiMode(option.value)}
                data-testid={`mailing-list-mode-${option.value}`}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>

        {uiMode === 'ONE_ID' ? (
          <div className="mailings-list-form-one">
            <Field label="ЕПГУ или УНТИ id ребёнка" htmlFor="mailing-list-one-id" required>
              <Input
                id="mailing-list-one-id"
                value={oneId}
                placeholder="7962327315"
                onChange={(event) => setOneId(event.target.value)}
                data-testid="mailing-list-one-id"
              />
            </Field>
            {oneId.trim().length > 0 ? (
              <p className="mailings-list-form-who" data-testid="mailing-list-who">
                {who.isFetching ? 'Ищем в реестре…' : formatWhoMatch(whoMatch)}
              </p>
            ) : null}
          </div>
        ) : null}

        {uiMode === 'IDS' ? (
          <Field
            label="Список id (в столбик или через запятую, до 1000)"
            htmlFor="mailing-list-ids"
            hint="ЕПГУ id или УНТИ id — api ищет по ЕПГУ, затем по УНТИ. Ненайденные вернутся в отчёте пересчёта."
          >
            <Textarea
              id="mailing-list-ids"
              rows={5}
              value={idsText}
              placeholder={'7962327315\n7962327316\n321693'}
              onChange={(event) => setIdsText(event.target.value)}
              data-testid="mailing-list-ids"
            />
          </Field>
        ) : null}

        {uiMode === 'STATUS' ? (
          <Field label="Статус воронки" htmlFor="mailing-list-status" required>
            <Select
              id="mailing-list-status"
              value={statusOne}
              onChange={(event) => setStatusOne(event.target.value)}
              data-testid="mailing-list-status"
            >
              {SELECTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABEL[status as keyof typeof STATUS_LABEL]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {uiMode === 'STATUSES' ? (
          <div className="mailings-list-form-statuses" data-testid="mailing-list-statuses">
            {SELECTION_STATUSES.map((status) => {
              const checked = statuses.includes(status);
              return (
                <label key={status} className="mailings-list-form-status">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setStatuses((prev) => (prev.includes(status) ? prev.filter((value) => value !== status) : [...prev, status]))
                    }
                    data-testid={`mailing-list-status-${status}`}
                  />
                  <span>{STATUS_LABEL[status as keyof typeof STATUS_LABEL]}</span>
                </label>
              );
            })}
          </div>
        ) : null}
      </fieldset>

      {shownError ? (
        <p className="mailings-list-form-error" role="alert" data-testid="mailing-list-form-error">
          {shownError}
        </p>
      ) : null}

      <div className="mailings-list-form-actions">
        <Button variant="primary" type="submit" loading={pending} data-testid="mailing-list-submit">
          {pending ? (mode === 'create' ? 'Создаём и считаем…' : 'Сохраняем и пересчитываем…') : submitLabel}
        </Button>
        {onCancel ? (
          <Button onClick={onCancel} data-testid="mailing-list-cancel">
            Отмена
          </Button>
        ) : null}
        <span className="mailings-list-form-note">
          {mode === 'create'
            ? 'Состав считается сразу — после создания откроется список получателей.'
            : 'Любое изменение пересчитывает состав списка.'}
        </span>
      </div>
    </form>
  );
}

/** The UI mode a stored list reopens in. */
function uiModeOf(list: MailingListDto): SelectionUiMode {
  if (list.selectionMode !== 'IDS') return list.selectionMode;
  return storedIds(list).length === 1 ? 'ONE_ID' : 'IDS';
}

function storedIds(list: MailingListDto): string[] {
  const ids = list.selectionPayload['ids'];
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

function storedStatuses(list: MailingListDto): string[] {
  const statuses = list.selectionPayload['statuses'];
  return Array.isArray(statuses) ? statuses.filter((status): status is string => typeof status === 'string') : [];
}

function firstStoredId(list: MailingListDto): string {
  return storedIds(list)[0] ?? '';
}
